import { Trans, useTranslation } from "react-i18next";

import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { EllipsisText } from "../../../../ellipsis-text/EllipsisText";
import { FormDialog } from "../../../../form-dialog/FormDialog";
import { formatShortDate } from "../../../../format/formatDate";
import { formatEuro } from "../../../../format/formatNumber";
import { CarryForwardRow } from "./CarryForwardBalancesDialog.types";
import { useCarryForwardBalances } from "./hooks/useCarryForwardBalances";

interface Props {
  clientId: string;
  onClose: () => void;
}

const DIALOG_WIDTH = 640;

export function CarryForwardBalancesDialog({ clientId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { today, rows, processableCount, skippedCount, confirm, isLoading, isSubmitting } = useCarryForwardBalances(
    clientId,
    onClose
  );

  const euro = (amount: number) => formatEuro(amount, i18n.language);

  return (
    <FormDialog
      title={t("claims.carryForward.dialogTitle")}
      onClose={onClose}
      // Nothing is drafted here — the overlay only restates the selection — so the buttons work off
      // the pending request rather than a form.
      formik={{ dirty: false, submitForm: confirm, isSubmitting, isValid: true, isValidating: false }}
      submitLabel={t("claims.carryForward.submit")}
      submitDisabled={processableCount === 0}
      confirmDirtyClose={false}
      disableErrorNotification
      fullWidth
      paperSx={{ maxWidth: { md: DIALOG_WIDTH, lg: DIALOG_WIDTH } }}
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress aria-label={t("claims.carryForward.loading")} />
        </Box>
      ) : (
        <Stack gap={3}>
          <Typography variant="body2" color="text.secondary">
            <Trans
              i18nKey="claims.carryForward.description"
              count={rows.length}
              values={{ date: formatShortDate(today) }}
              components={{ strong: <strong /> }}
            />
          </Typography>

          {skippedCount > 0 &&
            (processableCount === 0 ? (
              <Alert severity="warning">{t("claims.carryForward.nothingToCarryForward", { count: rows.length })}</Alert>
            ) : (
              <Alert severity="info">
                {t("claims.carryForward.skippedHint", { count: skippedCount, selected: rows.length })}
              </Alert>
            ))}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("claims.carryForward.creditor")}</TableCell>
                <TableCell align="right">{t("claims.carryForward.balance")}</TableCell>
                <TableCell align="right">{t("claims.carryForward.date")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <ClaimRow key={row.claim.id} row={row} today={today} euro={euro} />
              ))}
            </TableBody>
          </Table>
        </Stack>
      )}
    </FormDialog>
  );
}

interface RowProps {
  row: CarryForwardRow;
  today: Date;
  euro: (amount: number) => string;
}

function ClaimRow({ row, today, euro }: RowProps) {
  const { t } = useTranslation();
  const carriedForwardFrom = row.skipReason === null ? row.newestEntry : null;
  const color = carriedForwardFrom === null ? "text.disabled" : undefined;

  return (
    <TableRow>
      {/* maxWidth: 0 lets this column shrink below its text, so a long name truncates instead of widening the dialog. */}
      <TableCell sx={{ width: "100%", maxWidth: 0 }}>
        <EllipsisText value={row.claim.creditorName} variant="body2" fontWeight={600} color={color} />
      </TableCell>
      <TableCell align="right" sx={{ color, whiteSpace: "nowrap" }}>
        {euro(row.claim.currentSaldo)}
      </TableCell>
      <TableCell align="right" sx={{ color, whiteSpace: "nowrap" }}>
        {carriedForwardFrom === null ? (
          t(
            row.skipReason === "alreadyUpToDate"
              ? "claims.carryForward.skippedAlreadyUpToDate"
              : "claims.carryForward.skippedNoHistory"
          )
        ) : (
          <>
            <Box component="span" sx={{ color: "text.secondary" }}>
              {formatShortDate(carriedForwardFrom.date)}
            </Box>
            {" → "}
            <Box component="span" fontWeight={600}>
              {formatShortDate(today)}
            </Box>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}
